import { z } from 'zod';
const SUFFIX_MAX = 16;
/** Basic number presentation. Deliberately not a format-string language —
 *  an agent can only choose from these, so output is always predictable. */
export const NumberFormatSchema = z.object({
    style: z.enum(['plain', 'percent', 'currency']).default('plain'),
    decimals: z.number().int().min(0).max(6).default(0),
    /** Thousands grouping. */
    grouping: z.boolean().default(true),
    /** ISO 4217 code; only read when `style` is `currency`. */
    currency: z.string().length(3).default('USD'),
    /** Appended after the formatted number, e.g. " hrs". Display only. */
    suffix: z.string().max(SUFFIX_MAX).optional()
});
export const DEFAULT_NUMBER_FORMAT = {
    style: 'plain',
    decimals: 0,
    grouping: true,
    currency: 'USD'
};
/**
 * Units read as money. Deliberately a short fixed list, not every ISO 4217
 * code: KGS, CUP and ALL are currencies, but as a column's unit they mean
 * kilograms, cups and "all". Matched exactly, so "usd" stays a suffix.
 */
const COMMON_CURRENCIES = new Set([
    'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK',
    'MXN', 'BRL', 'INR', 'CNY', 'HKD', 'SGD', 'ZAR'
]);
/**
 * The starting format for a widget showing one column's numbers: what the
 * starter layout and the widget palette both use, so a column reads the same
 * however its widget was added. A common currency unit ("USD") becomes that
 * currency, with cents on a decimal column ("$14.99"); any other unit becomes a
 * suffix (" hrs"), dropped when too long to be a valid one.
 */
export function formatForColumn(column) {
    if (!column)
        return { ...DEFAULT_NUMBER_FORMAT };
    const unit = column.unit;
    if (unit && COMMON_CURRENCIES.has(unit)) {
        const minorUnits = new Intl.NumberFormat('en-US', { style: 'currency', currency: unit }).resolvedOptions().maximumFractionDigits ?? 2;
        return {
            ...DEFAULT_NUMBER_FORMAT,
            style: 'currency',
            currency: unit,
            decimals: column.type === 'integer' ? 0 : minorUnits
        };
    }
    const format = { ...DEFAULT_NUMBER_FORMAT, decimals: column.type === 'integer' ? 0 : 1 };
    if (unit && unit.length + 1 <= SUFFIX_MAX)
        format.suffix = ` ${unit}`;
    return format;
}
/** What is shown in place of a number that could not be computed. Using a dash
 *  rather than 0 is a correctness requirement, not a style choice. */
export const UNAVAILABLE_DISPLAY = '—';
/**
 * Cache of `Intl.NumberFormat` instances.
 *
 * Constructing one costs roughly 17µs; formatting with an existing one costs
 * roughly 0.7µs — a 25x difference that a table multiplies by every cell it
 * renders. Since view computation sits on the interactive path (the renderer
 * recomputes every widget whenever the definition changes), building a fresh
 * formatter per value showed up directly as lag while typing in the inspector.
 *
 * The key space is tiny and bounded — locale x 3 styles x 7 decimal counts x
 * 2 grouping flags x currency — so entries are reused almost immediately. The
 * cap only guards against an unexpected explosion of locales or currencies.
 */
const formatterCache = new Map();
const MAX_CACHED_FORMATTERS = 200;
function numberFormatter(format, locale) {
    // `suffix` is appended afterwards and must not be part of the key, or a
    // per-widget suffix would defeat the cache.
    const key = `${locale}|${format.style}|${format.decimals}|${format.grouping ? 1 : 0}|${format.currency}`;
    const cached = formatterCache.get(key);
    if (cached)
        return cached;
    const options = {
        minimumFractionDigits: format.decimals,
        maximumFractionDigits: format.decimals,
        useGrouping: format.grouping
    };
    if (format.style === 'percent') {
        options.style = 'percent';
    }
    else if (format.style === 'currency') {
        options.style = 'currency';
        options.currency = format.currency;
    }
    const formatter = new Intl.NumberFormat(locale, options);
    if (formatterCache.size >= MAX_CACHED_FORMATTERS)
        formatterCache.clear();
    formatterCache.set(key, formatter);
    return formatter;
}
export function formatNumber(value, format = DEFAULT_NUMBER_FORMAT, locale = 'en-US') {
    const text = numberFormatter(format, locale).format(value);
    return format.suffix ? `${text}${format.suffix}` : text;
}
/** Formats a metric result, rendering unavailability explicitly. */
export function formatMetricValue(result, format = DEFAULT_NUMBER_FORMAT, locale = 'en-US') {
    return result.available ? formatNumber(result.value, format, locale) : UNAVAILABLE_DISPLAY;
}
/** Formats a raw dataset cell for a table. Numbers use the column's format;
 *  everything else is stringified without interpretation. */
export function formatCell(value, format, locale = 'en-US') {
    if (value === null || value === undefined)
        return UNAVAILABLE_DISPLAY;
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            return UNAVAILABLE_DISPLAY;
        return formatNumber(value, format ?? DEFAULT_NUMBER_FORMAT, locale);
    }
    if (typeof value === 'boolean')
        return value ? 'Yes' : 'No';
    return String(value);
}
/** Short, human freshness text, e.g. "3 minutes ago". */
export function formatRelativeTime(iso, now = new Date()) {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime()))
        return 'unknown';
    const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
    if (seconds < 0)
        return 'just now';
    if (seconds < 45)
        return 'just now';
    const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
    if (seconds < 3600)
        return rtf.format(-Math.round(seconds / 60), 'minute');
    if (seconds < 86400)
        return rtf.format(-Math.round(seconds / 3600), 'hour');
    if (seconds < 2592000)
        return rtf.format(-Math.round(seconds / 86400), 'day');
    if (seconds < 31536000)
        return rtf.format(-Math.round(seconds / 2592000), 'month');
    return rtf.format(-Math.round(seconds / 31536000), 'year');
}
/** Date only, UTC, e.g. "Feb 21, 2026". UTC keeps the screen, the PDF and tests in agreement. */
export function formatDate(iso, locale = 'en-US') {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}
/** Absolute timestamp for print headers and provenance lines. */
export function formatTimestamp(iso, locale = 'en-US') {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
    }).format(date);
}
//# sourceMappingURL=format.js.map